import type { OrderStatus, ShopType } from '@reims-local/types';

const AUTH_BASE = import.meta.env.VITE_AUTH_URL ?? 'http://localhost:8787';
const SHOP_BASE = import.meta.env.VITE_SHOP_URL ?? 'http://localhost:8789';
const ORDER_BASE = import.meta.env.VITE_ORDER_URL ?? 'http://localhost:8791';
const RESERVATION_BASE = import.meta.env.VITE_RESERVATION_URL ?? 'http://localhost:8793';

async function request<T>(base: string, path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${base}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error((data as { error?: string }).error ?? `http_${res.status}`);
    (err as Error & { status?: number; data?: unknown }).status = res.status;
    (err as Error & { status?: number; data?: unknown }).data = data;
    throw err;
  }
  return data as T;
}

export interface PublicShopSummary {
  id: string;
  slug: string;
  name: string;
  shop_type: ShopType;
  description: string | null;
  address: string | null;
  city: string;
  logo_url: string | null;
  cover_url: string | null;
}

export const authApi = {
  me: () => request<{ ok: true; user: { id: string; email: string; role: string } }>(AUTH_BASE, '/auth/me').catch(() => null),
  magicLink: (email: string) =>
    request<{ ok: true }>(AUTH_BASE, '/auth/magic-link', { method: 'POST', body: JSON.stringify({ email }) }),
  verifyMagic: (token: string) =>
    request<{ ok: true; user: { id: string; email: string; role: string } }>(AUTH_BASE, '/auth/magic-link/verify', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
  logout: () => request<{ ok: true }>(AUTH_BASE, '/auth/logout', { method: 'POST' }),
};

export const shopApi = {
  listPublic: () => request<{ ok: true; data: PublicShopSummary[] }>(SHOP_BASE, '/public/shops'),
  getPublic: (slug: string) =>
    request<{
      ok: true;
      data: {
        shop: import('@reims-local/types').Shop;
        products: import('@reims-local/types').Product[];
      };
    }>(SHOP_BASE, `/public/shops/${encodeURIComponent(slug)}`),
};

export const orderApi = {
  getSlots: (slug: string) =>
    request<{ ok: true; data: Array<{ iso: string; label: string }> }>(
      ORDER_BASE,
      `/public/shops/${encodeURIComponent(slug)}/slots`,
    ),
  create: (input: {
    shop_slug: string;
    pickup_slot: string;
    items: Array<{ product_id: string; quantity: number }>;
    notes?: string;
    buyer_phone?: string;
  }) =>
    request<{ ok: true; data: { id: string; order_number: string; total_cents: number } }>(
      ORDER_BASE,
      '/orders',
      { method: 'POST', body: JSON.stringify(input) },
    ),
  listMine: () =>
    request<{
      ok: true;
      data: Array<{
        order: import('@reims-local/types').Order;
        shop: { id: string; name: string; slug: string };
      }>;
    }>(ORDER_BASE, '/orders/me'),
  get: (id: string) =>
    request<{
      ok: true;
      data: {
        order: import('@reims-local/types').Order;
        items: Array<import('@reims-local/types').OrderItem & { product: { name: string; category: string | null; photo_url: string | null } | null }>;
        shop: { id: string; name: string; slug: string; address: string | null; phone: string | null } | null;
      };
    }>(ORDER_BASE, `/orders/${id}`),
};

export type { OrderStatus };

export const reservationApi = {
  getSlots: (slug: string, date: string) =>
    request<{ ok: true; data: Array<{ iso: string; label: string; remaining: number }> }>(
      RESERVATION_BASE,
      `/public/shops/${encodeURIComponent(slug)}/reservation-slots?date=${date}`,
    ),
  create: (input: {
    shop_slug: string;
    date: string;
    time: string;
    covers: number;
    special_request?: string;
    buyer_phone?: string;
  }) =>
    request<{ ok: true; data: { id: string; reservation_number: string } }>(
      RESERVATION_BASE,
      '/reservations',
      { method: 'POST', body: JSON.stringify(input) },
    ),
  listMine: () =>
    request<{
      ok: true;
      data: Array<{
        reservation: import('@reims-local/types').Reservation;
        shop: { id: string; name: string; slug: string; address: string | null; phone: string | null };
      }>;
    }>(RESERVATION_BASE, '/reservations/me'),
  get: (id: string) =>
    request<{
      ok: true;
      data: {
        reservation: import('@reims-local/types').Reservation;
        shop: { id: string; name: string; slug: string; address: string | null; phone: string | null } | null;
      };
    }>(RESERVATION_BASE, `/reservations/${id}`),
  cancel: (id: string) =>
    request<{ ok: true }>(RESERVATION_BASE, `/reservations/${id}/cancel`, { method: 'POST' }),
};
